import { useCallback, useEffect, useState } from 'react';
import {
  DownloadOutlined,
  EyeFilled,
  RedoOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { Card, Row, Col, Statistic } from 'antd';
import { IClient, IInventoryEntry, IProduct } from '../../shared/interfaces';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';
import InventoryEntryCreate from './Create';
import { formatDate } from '../../shared/utils/format-date';
import { currency } from '../../shared/utils/functions';
import InventoryEntryView from './View';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const InventoryEntries: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [entries, setEntries] = useState<IInventoryEntry[]>([]);
  const [entry, setEntry] = useState<IInventoryEntry | null>(null);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [suppliers, setSuppliers] = useState<IClient[]>([]);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [modalView, setModalView] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listInventoryEntries = useCallback(async () => {
    setLoading(true);
    setEntries([]);
    try {
      const response = await api.post('/inventory-entries/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setEntries(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('inventoryEntriesPage.toast.errorTitle'),
        description: t('inventoryEntriesPage.toast.listEntries'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, t]);

  const listProducts = useCallback(async () => {
    await api
      .get('/products/select')
      .then((response) => {
        setProducts(response.data);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: t('inventoryEntriesPage.toast.errorTitle'),
          description: t('inventoryEntriesPage.toast.listProducts'),
        });
      });
  }, [addToast, t]);

  const listSuppliers = useCallback(async () => {
    await api
      .get(`/clients/select/${0}`)
      .then((response) => {
        setSuppliers(response.data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response.data.message}`,
        });
      });
  }, [addToast]);

  useEffect(() => {
    listInventoryEntries();
  }, [page, search, limit, listInventoryEntries]);

  const renderModalInventoryEntryCreate = () => {
    if (modalCreate)
      return (
        <InventoryEntryCreate
          open={modalCreate}
          close={() => setModalCreate(false)}
          setEntries={setEntries}
          products={products}
          suppliers={suppliers}
        />
      );
  };

  const renderModalInventoryView = () => {
    if (modalView)
      return (
        <InventoryEntryView
          open={modalView}
          close={() => setModalView(false)}
          entry={entry}
        />
      );
  };

  useEffect(() => {
    listProducts();
    listSuppliers();
  }, [listProducts, listSuppliers]);

  const totalValue = entries.reduce((sum, e) => +sum + (+e.value || 0), 0);

  return (
    <PageLayout>
      <PageIntro
        badge={t('inventoryEntriesPage.intro.badge')}
        icon={<DownloadOutlined />}
        title={t('inventoryEntriesPage.intro.title')}
        description={t('inventoryEntriesPage.intro.description')}
      />
      <MenuOptions
        showButtonBack
        menu_items={[
          {
            label: t('inventoryEntriesPage.actions.refresh'),
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listInventoryEntries(),
          },
          permissionFind([PermissionActions.inventoriesEntryCreate])
            ? {
                label: t('inventoryEntriesPage.actions.newEntry'),
                key: '2',
                icon: <PlusOutlined />,
                onClick: () => {
                  setEntry(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        {entries.length > 0 && (
          <Card style={{ marginBottom: 20 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title={t('inventoryEntriesPage.stats.registeredEntries')}
                  value={entries.length}
                  prefix={<DownloadOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title={t('inventoryEntriesPage.stats.purchasedValue')}
                  value={totalValue}
                  prefix="R$ "
                  precision={2}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title={t('inventoryEntriesPage.stats.receivedQuantity')}
                  value={entries.reduce((sum, e) => sum + (e.quantity || 0), 0)}
                  prefix={<DownloadOutlined style={{ marginRight: 4 }} />}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title={t('inventoryEntriesPage.stats.usedSuppliers')}
                  value={new Set(entries.map((e) => e.supplier_name)).size}
                />
              </Col>
            </Row>
          </Card>
        )}

        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: t('inventoryEntriesPage.table.date'),
              dataIndex: 'date',
              width: 120,
              render: (value: string | null) => (
                <>{value ? formatDate(value) : ''}</>
              ),
            },
            {
              title: t('inventoryEntriesPage.table.product'),
              dataIndex: 'product_name',
              width: 'auto',
            },
            {
              title: t('inventoryEntriesPage.table.source'),
              dataIndex: 'supplier_name',
              width: 'auto',
              render: (value: string | null) => <>{value ?? '-'}</>,
            },
            {
              title: t('inventoryEntriesPage.table.receivedQuantity'),
              dataIndex: 'quantity',
              width: 'auto',
              align: 'center' as const,
            },
            {
              title: t('inventoryEntriesPage.table.unitPrice'),
              dataIndex: 'value',
              width: 150,
              render: (value: number) => (
                <strong>{currency(value ?? 0)}</strong>
              ),
            },
            {
              title: t('inventoryEntriesPage.table.options'),
              width: 120,
              fixed: 'right' as const,
              render: (cell: IInventoryEntry) => (
                <DropdownOptions
                  items={[
                    {
                      label: t('inventoryEntriesPage.actions.view'),
                      key: '1',
                      icon: <EyeFilled />,
                      onClick: () => {
                        setEntry(cell);
                        setModalView(true);
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={entries}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalInventoryEntryCreate()}
      {renderModalInventoryView()}
    </PageLayout>
  );
};

export default InventoryEntries;
